/**
 * Main's two vitals feeds, rebuilt from the port's measurements (ndsUElC6).
 *
 * Main's vitals pages (VitalsPage, VitalsGroupPage, WatchPage, the
 * VitalSigns grid and the ECharts Graph) read two pacmny-be answers:
 *
 *  - `requestVitalSignsCodes`: the INTEGER ids of the vocabulary rows flagged
 *    as vital signs (`select id from "LA_labccd" where "ZV_labccd_vitalsign"`).
 *    The watch store and VitalSigns `parseInt` every entry and keep the
 *    numbers.
 *  - `/data/externalMeasures`: every reading on the chart, with no date range,
 *    as `{externalMeasures, externalMeasCcdMeasIds, externalMeasCcds}` -
 *    readings keyed by their id, reading ids grouped under the same integer
 *    vocabulary id, and that id's `{id, code, desc, src}`. Each reading names
 *    its time as `lotimestamp` (and `lodate`), which the grid's columns and
 *    the graph's x axis are built from, and its value as `value`.
 *
 * The port answered the first with LOINC objects - `parseInt` gave NaN for
 * every one, so no reading was ever a vital sign - and the second with one
 * 25-row page of raw rows that the pages destructured into three undefineds.
 * The grid was empty and the graph had nothing to plot.
 *
 * **One key for both.** The vocabulary row's id is `id` on a vital-sign code
 * and `concept_id` on a reading (pacmny-api VitalSignCodeResource,
 * MeasurementResource), so a code and its readings meet under the same
 * integer, exactly as main's `LA_labccd.id` joined them. Labels never come from
 * the number: VitalSigns resolves the LOINC through `ccds[key].code`, because
 * serial ids differ between environments.
 */

import api from './client';

/** The API's largest page. */
export const MEASUREMENT_PAGE_SIZE = 100;

/**
 * How many pages one read may follow: 100 000 readings, more than a year of
 * 15-minute continuous glucose. A cursor that never ends is a server fault,
 * and a chart cut short would show a trend that is not the patient's, so
 * reaching it refuses the read instead of answering with what arrived.
 */
export const MAX_MEASUREMENT_PAGES = 1000;

/** `requestVitalSignsCodes`' list: each code's integer key, in the API's (weight) order. */
export function vitalSignCodeKeys(codes) {
  return (codes || [])
    .map((code) => Number(code && code.id))
    .filter((key) => Number.isInteger(key));
}

function pad(n) {
  return String(n).padStart(2, '0');
}

/** `YYYY-MM-DD` and `YYYY-MM-DD HH:MM:SS` of an instant where the viewer is. */
function localParts(instant) {
  const when = new Date(instant);

  if (!instant || Number.isNaN(when.getTime())) return null;

  const date = `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
  const time = `${pad(when.getHours())}:${pad(when.getMinutes())}:${pad(when.getSeconds())}`;
  // pacmny-be's `loday`: whole days from 1900-01-01 to the reading's date.
  const day = Math.round(
    (Date.UTC(when.getFullYear(), when.getMonth(), when.getDate()) - Date.UTC(1900, 0, 1)) / 86400000,
  );

  return { date, timestamp: `${date} ${time}`, day };
}

/**
 * The value as the grid prints it and the graph plots it: what was reported.
 *
 * Main's `value` was `ZV_msrrvsn_value`, the text as reported, and the port's
 * is the same text (MeasurementResource `value`). `value_numeric` is NOT a
 * display value: pacmny-api's MeasurementValue strips a leading comparator to
 * get a number for a trend line, so "<0.5" is 0.5 there, ">600" (a meter
 * above its range) is 600 and ">60" (an eGFR) is 60, and "120/80" keeps only
 * its first number. Shown from it, a qualified result reads as an exact one.
 * Graph.vue plots `Number(value)` when it is finite, so a qualified or textual
 * result ("<0.5", "HI", "Negative") stays off the line, as main's did.
 *
 * Only when no text was reported (a C-CDA physical quantity has a number and
 * no display text) is the number shown: the DECIMAL(18,6) string with its
 * trailing zeros dropped from the string itself, never through a float.
 */
function legacyValue(row) {
  const reported = row.value;

  if (reported !== null && reported !== undefined && String(reported).trim() !== '') {
    return reported;
  }

  const numeric = row.value_numeric;

  if (numeric === null || numeric === undefined || String(numeric).trim() === '') {
    return reported === undefined ? null : reported;
  }

  const text = String(numeric).trim();

  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
}

/**
 * Main's data-source names (`A_datasource.name`), from the port's slugs
 * (DataSourceSeeder `code`). Main's uploaders each minted their own string,
 * so two are SCREAMING_SNAKE - the ones MeasuresGrid's device icons switch on
 * - and the rest Title Case, which the grid shows as the icon's tooltip. The
 * port's own sources (`ccda_import`, `report_extraction`) had no data source
 * in main at all, so they name none here either, rather than a slug no main
 * screen ever showed.
 */
const LEGACY_SOURCE_NAMES = {
  fora_device: 'Fora Device',
  manual_entry: 'Manual Entry',
  blood_pressure_device: 'BLOOD_PRESSURE_DEVICE',
  weight_blood_pressure_device: 'WEIGHT_BLOOD_PRESSURE_DEVICE',
  fore_device: 'Fore Device',
  freestyle_librelink: 'FreeStyle LibreLink',
};

function legacySource(code) {
  return code ? (LEGACY_SOURCE_NAMES[String(code).toLowerCase()] || null) : null;
}

/**
 * `{externalMeasures, externalMeasCcdMeasIds, externalMeasCcds}` from
 * measurement rows.
 *
 * Times are the viewer's wall clock, as pacmny-be's `timestamp without time
 * zone` read back: the API's instants are UTC, and an evening reading west of
 * Greenwich would otherwise land in tomorrow's grid column. `weight` (main's
 * `ZV_labccd_weight`, the order the grid lists vitals in) comes from the
 * vital-sign codes when they were read; any other measured thing has none.
 */
export function externalMeasuresLegacyShape(rows, codes = []) {
  const weights = {};
  (codes || []).forEach((code) => {
    if (code && Number.isInteger(Number(code.id))) weights[Number(code.id)] = Number(code.weight) || 0;
  });

  const externalMeasures = {};
  const externalMeasCcdMeasIds = {};
  const externalMeasCcds = {};

  (rows || []).forEach((row) => {
    const key = Number(row && row.concept_id);
    const lo = localParts(row && row.measured_at);

    // Every row the API sends has a concept (the column is NOT NULL) and a
    // time; a row without either cannot be placed in a grid or on an axis.
    if (!Number.isInteger(key) || !lo || externalMeasures[row.id]) return;

    const hi = localParts(row.measured_to) || lo;

    externalMeasures[row.id] = {
      id: row.id,
      value: legacyValue(row),
      units: row.unit === undefined ? null : row.unit,
      lodate: lo.date,
      loday: lo.day,
      lotimestamp: lo.timestamp,
      hidate: hi.date,
      hiday: hi.day,
      hitimestamp: hi.timestamp,
      ccd: key,
      codes: [key],
      weight: weights[key] || 0,
      source: legacySource(row.source),
      recent: (localParts(row.recorded_at) || lo).timestamp,
    };

    (externalMeasCcdMeasIds[key] = externalMeasCcdMeasIds[key] || []).push(row.id);

    if (!externalMeasCcds[key]) {
      externalMeasCcds[key] = {
        id: key,
        code: row.code || '',
        desc: row.display || row.code || '',
        src: row.code_system || '',
      };
    }
  });

  return { externalMeasures, externalMeasCcdMeasIds, externalMeasCcds };
}

/**
 * Every reading on the chart, newest first, following the cursor to its end.
 *
 * Main returned every measure with no date range (VitalsPage asks with no
 * arguments); a window is passed through only when a caller sends one, under
 * the names the API validates (`from`/`to`).
 */
export async function listAllMeasurements(patientId, params = {}, deps = {}) {
  const client = deps.api || api;
  const rows = [];
  let cursor = null;

  for (let page = 0; page < MAX_MEASUREMENT_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await client.list(`/patients/${patientId}/measurements`, {
      ...params,
      per_page: MEASUREMENT_PAGE_SIZE,
      ...(cursor ? { cursor } : {}),
    });

    rows.push(...((result && result.items) || []));
    cursor = (result && result.meta && result.meta.next_cursor) || null;

    if (!cursor) return rows;
  }

  throw new Error(
    `The chart has more than ${MAX_MEASUREMENT_PAGES * MEASUREMENT_PAGE_SIZE} readings; `
    + 'they were not shown, because a part of them would read as the whole trend.',
  );
}

/**
 * pacmny-be's `/data/externalMeasures` answer for a chart.
 *
 * The vital-sign codes are read alongside for each code's display weight; the
 * list is a day-cacheable vocabulary read, and failing it costs the grid its
 * ordering, not its readings.
 *
 * The answer also names the chart it was read for (`patientId`, beside main's
 * three objects). Main's chart entry replaced the whole model, so its
 * measures could never belong to another chart; the port keeps them in the
 * store across a chart switch, and a long chart is many requests, so the
 * store (login `addMeasures`) needs to know whose readings arrived to refuse
 * a late answer for a chart no longer in view.
 */
export async function readExternalMeasures(patientId, p = {}, deps = {}) {
  const client = deps.api || api;

  if (patientId === null || patientId === undefined || patientId === '') {
    return { ...externalMeasuresLegacyShape([]), patientId: null };
  }

  const range = {};
  const from = p.startDate || p.from;
  const to = p.endDate || p.to;
  if (from) range.from = String(from).slice(0, 10);
  if (to) range.to = String(to).slice(0, 10);

  const [rows, codes] = await Promise.all([
    listAllMeasurements(patientId, range, { api: client }),
    client.list('/vital-sign-codes').then((page) => (page && page.items) || []).catch(() => []),
  ]);

  return { ...externalMeasuresLegacyShape(rows, codes), patientId };
}

export default {
  vitalSignCodeKeys, externalMeasuresLegacyShape, listAllMeasurements, readExternalMeasures,
};
